import { createAuthenticatedApi } from "@/lib/utils";
import { parseYmdLocal } from "@/lib/utils/date/formatYmdLocal";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import type {
  GetApprovedMakeupRosterEntryResponse,
  GetMakeupResponse,
  MakeupCancellationRequest,
  MakeupChangeRequest,
  MakeupRequest,
  MakeupScrollFilterRequest,
  MakeupStatus,
  Origin,
  RejectMakeupChangeRequest,
  RejectMakeupRequest,
} from "@/openapi/generated";
import { MakeupSessionsApi } from "@/openapi/generated";

export const MAKEUP_SESSIONS_PAGE_SIZE = 30;

/** Member portal: one search for every student on the logged-in parent. */
export const MEMBER_MAKEUP_SESSIONS_PAGE_SIZE = 100;

/** Normalized roster entry — OpenAPI regen may type ids as GoogleLoginCallbackTidParameter. */
export type ApprovedMakeupRosterEntry = {
  makeupSessionId: string;
  studentId: number;
  studentName: string;
  approvedDate: string;
  approvedStartTime: string;
  approvedEndTime: string;
  origin: Origin;
};

function toApprovedMakeupRosterEntry(
  entry: GetApprovedMakeupRosterEntryResponse,
): ApprovedMakeupRosterEntry {
  return {
    makeupSessionId: String(entry.makeupSessionId),
    studentId: Number(entry.studentId),
    studentName: String(entry.studentName),
    approvedDate:
      toMakeupFilterDate(entry.approvedDate as Date | string) ??
      String(entry.approvedDate),
    approvedStartTime: String(entry.approvedStartTime),
    approvedEndTime: String(entry.approvedEndTime),
    origin: entry.origin,
  };
}

export type MakeupSearchFilters = {
  memberId?: number | null;
  studentId?: number | null;
  teacherId?: number | null;
  locationId?: number | null;
  sourceClassId?: number | null;
  makeupClassId?: number | null;
  sourceAppointmentId?: number | null;
  makeupAppointmentId?: number | null;
  status?: MakeupStatus | null;
  fromDate?: Date | null;
  toDate?: Date | null;
};

function tenantId(): number {
  return resolveEffectiveTenantNumber();
}

/** Backend makeup search accepts calendar days only (`YYYY-MM-DD`), not ISO datetimes. */
function toMakeupFilterDate(value?: Date | string | null): string | null {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return null;
    return toMakeupFilterDate(parsed);
  }
  if (Number.isNaN(value.getTime())) return null;
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function toMakeupMemberId(
  userId: number | string | null | undefined,
): number | null {
  const id = Number(userId);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function toScrollFilter(
  filters: MakeupSearchFilters,
): MakeupScrollFilterRequest {
  return {
    memberId: filters.memberId ?? null,
    studentId: filters.studentId ?? null,
    teacherId: filters.teacherId ?? null,
    locationId: filters.locationId ?? null,
    sourceClassId: filters.sourceClassId ?? null,
    makeupClassId: filters.makeupClassId ?? null,
    sourceAppointmentId: filters.sourceAppointmentId ?? null,
    makeupAppointmentId: filters.makeupAppointmentId ?? null,
    status: filters.status ?? null,
    fromDate: toMakeupFilterDate(filters.fromDate),
    toDate: toMakeupFilterDate(filters.toDate),
  };
}

const MAKEUP_SCROLL_FETCH_MAX_PAGES = 100;

/** Scroll through all pages for client-side student-name filtering. */
export async function fetchAllMakeupSessionsScroll(
  accessToken: string,
  filters: MakeupSearchFilters = {},
): Promise<GetMakeupResponse[]> {
  const all: GetMakeupResponse[] = [];
  let page = 1;
  let lastSelectedId: string | undefined;

  for (let i = 0; i < MAKEUP_SCROLL_FETCH_MAX_PAGES; i++) {
    const result = await searchMakeupSessions(accessToken, {
      page,
      pageSize: MAKEUP_SESSIONS_PAGE_SIZE,
      lastSelectedId,
      filters,
    });
    const batch = result.items ?? [];
    all.push(...batch);
    if (result.hasNextPage !== true) break;
    const nextCursor =
      result.lastSelectedId != null && result.lastSelectedId !== ""
        ? String(result.lastSelectedId)
        : undefined;
    if (!nextCursor) break;
    lastSelectedId = nextCursor;
    page += 1;
  }

  return all;
}

export async function searchMakeupSessions(
  accessToken: string,
  {
    page = 1,
    pageSize = MAKEUP_SESSIONS_PAGE_SIZE,
    lastSelectedId,
    active,
    filters = {},
  }: {
    page?: number;
    pageSize?: number;
    lastSelectedId?: string;
    active?: boolean;
    filters?: MakeupSearchFilters;
  },
) {
  const service = createAuthenticatedApi(MakeupSessionsApi, accessToken);
  return service.apiMakeupSessionsSearchPost({
    page,
    pageSize,
    lastSelectedId,
    active,
    xTenantID: tenantId(),
    makeupScrollFilterRequest: toScrollFilter(filters),
  });
}

export async function getMakeupSession(
  accessToken: string,
  id: string,
): Promise<GetMakeupResponse> {
  const service = createAuthenticatedApi(MakeupSessionsApi, accessToken);
  return service.apiMakeupSessionsIdGet({ id, xTenantID: tenantId() });
}

export async function createMakeupSession(
  accessToken: string,
  makeupRequest: MakeupRequest,
) {
  const service = createAuthenticatedApi(MakeupSessionsApi, accessToken);
  return service.apiMakeupSessionsPost({
    makeupRequest,
    xTenantID: tenantId(),
  });
}

export async function approveMakeupSession(accessToken: string, id: string) {
  const service = createAuthenticatedApi(MakeupSessionsApi, accessToken);
  return service.apiMakeupSessionsIdApprovePut({ id, xTenantID: tenantId() });
}

export async function rejectMakeupSession(
  accessToken: string,
  id: string,
  rejectMakeupRequest: RejectMakeupRequest,
) {
  const service = createAuthenticatedApi(MakeupSessionsApi, accessToken);
  return service.apiMakeupSessionsIdRejectPut({
    id,
    rejectMakeupRequest,
    xTenantID: tenantId(),
  });
}

export async function getApprovedMakeupsForClassDate(
  accessToken: string,
  classId: number,
  sessionDateYmd: string,
): Promise<ApprovedMakeupRosterEntry[]> {
  const service = createAuthenticatedApi(MakeupSessionsApi, accessToken);
  const entries = await service.apiMakeupSessionsApprovedForClassClassIdGet({
    classId,
    // OpenAPI `format: date` regen types this as Date in CI.
    date: parseYmdLocal(sessionDateYmd),
    xTenantID: tenantId(),
  });
  return entries.map(toApprovedMakeupRosterEntry);
}

export async function requestMakeupChange(
  accessToken: string,
  id: string,
  makeupChangeRequest: MakeupChangeRequest,
) {
  const service = createAuthenticatedApi(MakeupSessionsApi, accessToken);
  return service.apiMakeupSessionsIdRequestChangePut({
    id,
    makeupChangeRequest,
    xTenantID: tenantId(),
  });
}

export async function acceptMakeupChange(accessToken: string, id: string) {
  const service = createAuthenticatedApi(MakeupSessionsApi, accessToken);
  return service.apiMakeupSessionsIdAcceptChangePut({
    id,
    xTenantID: tenantId(),
  });
}

export async function rejectMakeupChange(
  accessToken: string,
  id: string,
  rejectMakeupChangeRequest: RejectMakeupChangeRequest,
) {
  const service = createAuthenticatedApi(MakeupSessionsApi, accessToken);
  return service.apiMakeupSessionsIdRejectChangePut({
    id,
    rejectMakeupChangeRequest,
    xTenantID: tenantId(),
  });
}

export async function cancelMakeupSession(
  accessToken: string,
  id: string,
  makeupCancellationRequest: MakeupCancellationRequest,
) {
  const service = createAuthenticatedApi(MakeupSessionsApi, accessToken);
  return service.apiMakeupSessionsIdCancelPut({
    id,
    makeupCancellationRequest,
    xTenantID: tenantId(),
  });
}

export async function deleteMakeupSession(accessToken: string, id: string) {
  const service = createAuthenticatedApi(MakeupSessionsApi, accessToken);
  return service.apiMakeupSessionsIdDelete({ id, xTenantID: tenantId() });
}
